import React, { useMemo, useRef } from 'react';

import flatpickr from 'flatpickr';
import Flatpickr from 'react-flatpickr';
import './DatePicker.scoped.scss';
import 'flatpickr/dist/themes/light.css';
import { DateTime } from 'luxon';
import classNames from 'classnames';

import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';

interface Props {
  value?: string; // Should be as '2022-03-25' or '2022-03-25T12:00:59.609+03:00'
  minDate?: 'today' | string; // Should be as 'today' or '2000-03-25'
  maxDate?: 'today' | string; // Should be as 'today' or '2000-03-25'
  onChange: ({ dateStr, dateTime }: { dateStr: string; dateTime: DateTime | null }) => void;
  error?: string;
  className?: string;
  inputClassName?: string;
  options?: Partial<flatpickr.Options.Options>;
}

const DatePicker: React.FC<Props> = ({
  onChange,
  value = '',
  minDate,
  maxDate,
  options = {},
  error,
  className,
  inputClassName,
}) => {
  const datePickerRef = useRef(null);

  const handleOnChange = dates => {
    if (dates.length === 0) {
      onChange({ dateStr: '', dateTime: null });
    }
    const date = dates[0];
    const dateTime = DateTime.fromJSDate(date);
    const hasTimeComponent = value && value.includes('T');
    const timeComponent = hasTimeComponent ? `T${value.split('T')[1]}` : '';
    const dateStr = dateTime.toISODate() + timeComponent;

    onChange({ dateStr, dateTime });
  };

  const handleOnClose = () => {
    if (!datePickerRef.current.node.value) {
      onChange({ dateStr: '', dateTime: null });
    } else {
      datePickerRef.current.node.blur();
    }
  };

  const handleIconClick = () => {
    datePickerRef.current.node.focus();
  };

  const datePickerOptions = (() => {
    const result: Partial<flatpickr.Options.Options> = {
      dateFormat: 'n/j/Y',
      allowInput: true,
      onChange: handleOnChange,
      onClose: handleOnClose,
      ...options,
    };

    if (minDate) {
      result.minDate = minDate === 'today' ? 'today' : `${minDate}T00:00:00.000Z`;
    }
    if (maxDate) {
      result.maxDate = maxDate === 'today' ? 'today' : `${maxDate}T00:00:00.000Z`;
    }

    return result;
  })();

  const valueForDatePicker = useMemo(() => {
    if (!value) {
      return '';
    }
    if (value.includes('T')) {
      return `${value.split('T')[0]}T00:00:00.000Z`;
    }
    return `${value}T00:00:00.000Z`;
  }, [value]);

  return (
    <div className={classNames('container', { [className]: !!className })}>
      <div className="input-container">
        <Flatpickr
          ref={datePickerRef}
          value={valueForDatePicker}
          options={datePickerOptions}
          className={inputClassName}
        />
        <div className="icon" onClick={handleIconClick}>
          <CalendarIcon />
        </div>
      </div>
      {error && <p className="error">{error}</p>}
    </div>
  );
};

export default DatePicker;
